import React, { useCallback, useState, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import { useNavigate, useLocation } from "react-router-dom";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import { useDispatch } from "react-redux";
import { Toast, Form, Col, Row, Card } from "react-bootstrap";
import { Formik } from "formik";
import { addGallery } from "../../modules/GalleryImage/gallerySlice";
import SimpleDropdown from "../../components/simpleDropdown";
import { getAllCategories } from "../GalleryCategory/galleryCategorySlice";
import Editor from "../../components/commonEditor";
import { galleryVideoSchema } from "../../schema/validationSchemas";

export default function Add() {
  const navigate = useNavigate();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const dispatch = useDispatch();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentPage = params.get("page") || 1;
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const selected_organization_id =
    pathSegments.length > 1 && pathSegments[0] === "organization"
      ? pathSegments[1]
      : null;

  const initialEventState = {
    organization_id: selected_organization_id,
    gallery_title: "",
    video_link: "",
    gallery_category_id: 1,
    caption: "",
    description: "",
    type: "video",
  };

  const handleModalClose = () => {
    navigate(
      `/organization/${selected_organization_id}/gallery-videos?page=${currentPage}`
    );
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    await dispatch(addGallery(values))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem(
            "gallery_video_notify_message",
            res.payload.message
          );
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          navigate(`/organization/${selected_organization_id}/gallery-videos`);
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || "Failed to add gallery!");
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setSnackbarMessage("Failed to add gallery!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  const fetchCategories = useCallback(async () => {
    try {
      const payload = {
        organization_id: selected_organization_id,
      };
      await dispatch(getAllCategories(payload)).then((res) => {
        if (res.payload.success) {
          if (res.payload.data.length > 0) {
            const formattedData = res.payload.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            setCategoryOptions(formattedData);
          }
        } else {
          setCategoryOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch, selected_organization_id]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Formik
        initialValues={initialEventState}
        validationSchema={galleryVideoSchema}
        onSubmit={handleSubmit}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="bg-white p-4 rounded-2 shadow-sm"
          >
            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
              <Row>
                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="gallery_title" title="Title" />
                  <Input
                    id="gallery_title"
                    name="gallery_title"
                    type="text"
                    placeholder="Enter Video Title"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.gallery_title && touched.gallery_title
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.gallery_title && errors?.gallery_title && (
                    <ValidationFeedback title={errors?.gallery_title} />
                  )}
                </Col>

                <Col md={6} className="">
                  <SimpleDropdown
                    label="Category (Optional)"
                    name="gallery_category_id"
                    value={values.gallery_category_id}
                    onChange={(selectedOption) =>
                      setFieldValue("gallery_category_id", selectedOption.value)
                    }
                    options={categoryOptions}
                    placeholder="Select a Category"
                    error={errors?.gallery_category_id}
                    touched={touched.gallery_category_id}
                    className={"mb-4"}
                  />
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="caption" title="Caption" />
                  <Input
                    id="caption"
                    name="caption"
                    type="text"
                    placeholder="Enter Video Caption"
                    onChange={handleChange}
                    className={`shadow-none`}
                  />
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="video_link" title="Video Link" />
                  <Input
                    id="video_link"
                    name="video_link"
                    type="text"
                    placeholder="Enter Video Link"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.video_link && touched.video_link
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.video_link && errors?.video_link && (
                    <ValidationFeedback title={errors?.video_link} />
                  )}
                </Col>

                <div className="mb-4">
                  <CommonFormLabel
                    htmlFor="description"
                    title="Video Description"
                  />
                  <Editor
                    value={values.description}
                    onChange={(content) =>
                      setFieldValue("description", content)
                    }
                  />
                  {touched.description && errors?.description && (
                    <ValidationFeedback title={errors?.description} />
                  )}
                </div>
              </Row>
            </Card>

            <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
              <CommonButton
                title={isSubmitting ? "Adding..." : "Add"}
                type="submit"
                disabled={isSubmitting}
                className="btn-primary primary-shadow"
              />
              <CommonButton
                title="Cancel"
                onClick={handleModalClose}
                className="btn-muted"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
